// src/pages/ViewFolderSharePage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { FlexGroup, FlexItem, Icon, SidePanel, Text, Tooltip } from '@zenqms/zui';
import shareSlice, { fetchFolder, fetchShares } from '../share/share-slice';
import { FolderTree } from '../components/FolderTree/FolderTree';
import { FolderTreeErrorBoundary } from '../components/FolderTree/FolderTreeErrorBoundary';
import { FolderBreadcrumb } from '../components/FolderBreadcrumb/FolderBreadcrumb';
import { FolderContentArea } from '../components/FolderContentArea/FolderContentArea';
import { findShareForFolder } from '../components/FolderTree/buildTreeNodes';
import './ViewFolderSharePage.scss';

// Suppress SidePanel's own toggle button — we render ours inline in the header
const NO_TOGGLE = { render: () => null };

export function ViewFolderSharePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const isRecoveringRef = useRef(false);

  // { folderShareId: string, folderId: string } | null
  const [selectedFolder, setSelectedFolder] = useState(null);

  const shares = useSelector((state) => state.share.shares);
  const subfolders = useSelector((state) => state.share.subfolders);
  const folders = useSelector((state) => state.share.folders);
  const inaccessibleFolderIds = useSelector((state) => state.share.inaccessibleFolderIds);
  const hasLoaded = useSelector((state) => state.share.hasLoaded);

  useEffect(() => {
    dispatch(fetchShares({ navigate }));
  }, [dispatch, navigate]);

  // Auto-select the first root folder once shares load, if nothing is selected yet.
  // shares[0].folderId is the root of that share. fetchFolder populates the breadcrumb path.
  // Skip any folder that was pruned after being detected as deleted/revoked, so we never
  // land back on a folder that no longer exists.
  useEffect(() => {
    if (selectedFolder !== null) return;
    if (shares.length === 0) return;

    const first = shares.find(({ folderId }) => !inaccessibleFolderIds.includes(folderId));
    if (!first) return;
    setSelectedFolder({ folderShareId: first.folderShareId, folderId: first.folderId });
    dispatch(fetchFolder({ folderShareId: first.folderShareId, folderId: first.folderId, navigate }));
  }, [shares, selectedFolder, inaccessibleFolderIds, dispatch, navigate]);

  const breadcrumbPath = (() => {
    if (!selectedFolder) return [];
    const fullPath = folders[selectedFolder.folderId]?.path ?? [];
    const accessibleShareFolderIds = new Set(
      shares.map(({ folderId }) => folderId).filter((id) => !inaccessibleFolderIds.includes(id))
    );
    const rootIdx = fullPath.findIndex(({ id }) => accessibleShareFolderIds.has(id));
    return rootIdx >= 0 ? fullPath.slice(rootIdx) : fullPath;
  })();

  const selectedShare = shares.find((s) => s.folderShareId === selectedFolder?.folderShareId);
  const allowDownload = selectedShare?.allowDownload ?? false;

  useEffect(() => {
    if (!hasLoaded) return;
    if (shares.length === 0 || shares.every(({ folderId }) => inaccessibleFolderIds.includes(folderId))) {
      navigate('/expired');
    }
  }, [shares, inaccessibleFolderIds, hasLoaded, navigate]);

  function handleFolderSelect(folderId) {
    if (!folderId) return;

    const share = findShareForFolder(folderId, shares, subfolders);
    if (!share) return;

    const { folderShareId } = share;
    setSelectedFolder({ folderShareId, folderId });
    dispatch(fetchFolder({ folderShareId, folderId, navigate }));
  }

  async function tryLoadFolder(folderShareId, folderId) {
    try {
      await dispatch(fetchFolder({ folderShareId, folderId, navigate })).unwrap();
      return true;
    } catch {
      return false;
    }
  }

  async function handleFolderGone() {
    if (!selectedFolder) return;
    if (isRecoveringRef.current) return;
    isRecoveringRef.current = true;

    try {
      const goneFolderId = selectedFolder.folderId;
      dispatch(shareSlice.actions.markFolderInaccessible({ folderId: goneFolderId }));

      const ancestorIds = breadcrumbPath
        .slice(0, -1)
        .map(({ id }) => id)
        .reverse();
      const shareRootIds = shares.map(({ folderId }) => folderId);

      const seen = new Set([goneFolderId]);
      const candidateIds = [...ancestorIds, ...shareRootIds].filter((id) => {
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      for (const candidateId of candidateIds) {
        if (inaccessibleFolderIds.includes(candidateId)) continue;

        const share = findShareForFolder(candidateId, shares, subfolders);
        if (!share) continue;

        const loaded = await tryLoadFolder(share.folderShareId, candidateId);
        if (loaded) {
          setSelectedFolder({ folderShareId: share.folderShareId, folderId: candidateId });
          return;
        }
      }

      setSelectedFolder(null);
    } finally {
      isRecoveringRef.current = false;
    }
  }

  function handleBreadcrumbNavigate(folderId) {
    if (!selectedFolder) return;
    const share = findShareForFolder(folderId, shares, subfolders);
    const folderShareId = share ? share.folderShareId : selectedFolder.folderShareId;
    setSelectedFolder({ folderShareId, folderId });
    dispatch(fetchFolder({ folderShareId, folderId, navigate }));
  }

  return (
    <FlexGroup direction="row" className="view-folder-share-page">
      {/* ── Collapsible sidebar (ZUI SidePanel, custom toggle placement) ── */}
      <SidePanel
        side="left"
        open={isSidebarOpen}
        onOpenChange={setIsSidebarOpen}
        width={260}
        label={formatMessage({ id: 'folderTree.sidebar.title', defaultMessage: 'Shared Folders' })}
        className="view-folder-share-sidepanel"
        toggleButton={NO_TOGGLE}
        collapsedContent={
          /* Collapsed strip: expand chevron centred */
          <FlexGroup direction="column" alignItems="center" className="vfsp-collapsed-bar" height="100%">
            <Tooltip
              content={formatMessage({ id: 'folderTree.sidebar.expand', defaultMessage: 'Expand sidebar' })}
              position="right center"
              on={['hover', 'focus']}
              trigger={
                <button
                  type="button"
                  className="vfsp-toggle-btn"
                  onClick={() => setIsSidebarOpen(true)}
                  aria-label={formatMessage({ id: 'folderTree.sidebar.expand', defaultMessage: 'Expand sidebar' })}
                >
                  <Icon name="chevron-right" noHover />
                </button>
              }
            />
          </FlexGroup>
        }
        collapsedWidth={48}
      >
        {/* Expanded panel */}
        <FlexGroup direction="column" className="vfsp-sidebar-inner">
          {/* Header row: title + collapse chevron on the same row */}
          <FlexGroup alignItems="center" justifyContent="space-between" className="vfsp-sidebar-header">
            <Text as="span" weight="bold" size="md">
              {formatMessage({ id: 'folderTree.sidebar.title', defaultMessage: 'Shared Folders' })}
            </Text>
            <Tooltip
              content={formatMessage({ id: 'folderTree.sidebar.collapse', defaultMessage: 'Collapse sidebar' })}
              position="right center"
              on={['hover', 'focus']}
              trigger={
                <button
                  type="button"
                  className="vfsp-toggle-btn"
                  onClick={() => setIsSidebarOpen(false)}
                  aria-label={formatMessage({
                    id: 'folderTree.sidebar.collapse',
                    defaultMessage: 'Collapse sidebar',
                  })}
                >
                  <Icon name="chevron-left" noHover />
                </button>
              }
            />
          </FlexGroup>

          <FolderTreeErrorBoundary
            fallbackMessage={formatMessage({
              id: 'folderTree.errorBoundaryFallback',
              defaultMessage: 'The folder tree could not be displayed. Please refresh the page.',
            })}
          >
            <FolderTree onSelect={handleFolderSelect} selectedId={selectedFolder?.folderId ?? null} />
          </FolderTreeErrorBoundary>
        </FlexGroup>
      </SidePanel>

      {/* ── Main content area ───────────────────────────────────────────── */}
      <FlexItem grow={1} className="view-folder-share-content">
        <FolderBreadcrumb folderPath={breadcrumbPath} onNavigate={handleBreadcrumbNavigate} />
        <FolderContentArea
          folderShareId={selectedFolder?.folderShareId ?? null}
          folderId={selectedFolder?.folderId ?? null}
          folderName={breadcrumbPath[breadcrumbPath.length - 1]?.name ?? ''}
          allowDownload={allowDownload}
          onFolderGone={handleFolderGone}
        />
      </FlexItem>
    </FlexGroup>
  );
}
